/**
 * Mana Locals Admin — M05 foundation boundary schemas.
 *
 * CONTRACT SOURCE: the API repository's M05 entitlement read
 * (`GET /api/v1/foundation/entitlements`).
 *
 * WHY THE ADMIN READS ENTITLEMENTS
 * --------------------------------
 * The session projection reports AUTHORITY: what the actor may do. Entitlements are
 * something else — what the TENANT has been given. A salon that has not bought the
 * booking capability has no booking screen, even though its owner holds the booking
 * permission; and a platform administrator holds the platform permission but has no
 * tenant entitlements at all.
 *
 * Keeping the two apart is the point: mixing them would either hide a screen the
 * tenant has paid for, or offer a screen it has not.
 */
import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

/**
 * The scope an entitlement applies to.
 *
 * `tenant` is the whole tenant; `location` narrows it to one location. The admin
 * does not interpret the scope — it only checks that the entitlement is usable — so
 * the shape is parsed and carried, not interpreted.
 */
export const EntitlementScopeSchema = z
  .object({
    type: z.string().min(1),
    locationId: UuidSchema.nullable().optional(),
  })
  .passthrough();

/**
 * One entitlement.
 *
 * `state` and `usable` are BOTH checked by the admin, and they are not the same
 * thing: an entitlement can be granted and not yet usable (a future effective date),
 * and a revoked one must never reach the UI at all.
 */
export const EntitlementSchema = z
  .object({
    id: UuidSchema,
    tenantId: UuidSchema,
    /** The capability id, e.g. `basic_queue`, `retail_basic`. */
    capability: z.string().min(1),
    scope: EntitlementScopeSchema,
    state: z.string().min(1),
    usable: z.boolean(),
    value: z.string().nullable().optional(),
    effectiveFrom: IsoDateTimeSchema.nullable().optional(),
    effectiveTo: IsoDateTimeSchema.nullable().optional(),
    revokedAt: IsoDateTimeSchema.nullable().optional(),
    version: z.number().int().min(1),
    updatedAt: IsoDateTimeSchema,
  })
  .passthrough();

export const EntitlementsResponseSchema = z.object({
  data: z.array(EntitlementSchema),
});

export type Entitlement = z.infer<typeof EntitlementSchema>;
